import { get } from 'lodash'

import getErrorMessage from 'utils/getErrorMessage'
import { recordPageAction } from 'newrelic/newrelic-telemetry'

/**
 * Kiosk diagnostics.
 *
 * Kiosks run unattended on customer hardware and their operators can rarely report
 * anything more precise than "it didn't work", which leaves support with server logs
 * that only prove a roster was served. These events exist to answer the questions
 * those logs cannot: how big was the roster the kiosk actually held, how long had it
 * been failing to refresh, and when somebody could not find themselves, were they
 * simply looking at the wrong tab?
 *
 * Nothing identifying is sent - no names, emails, phone numbers or search text, only
 * counts and the length of what was typed.
 */

// Roster refresh failures are invisible in the UI and the poll backs off to a
// 30 minute interval, so a kiosk can sit on a stale list for a long time. Tracking
// the last good load lets a single failure event carry that history with it.
let lastLoadedAt = null
let consecutiveFailures = 0

// Session counters let support watch a live kiosk (uptime + activity) without
// tailing API logs. Reset when a kiosk session starts; final snapshot on stop.
let sessionStartedAt = null
let checkInsThisSession = 0
let checkOutsThisSession = 0

export const SESSION_HEARTBEAT_MS = 1000 * 60 * 5

export const resetRosterDiagnostics = () => {
  lastLoadedAt = null
  consecutiveFailures = 0
}

export const resetSessionDiagnostics = () => {
  sessionStartedAt = Date.now()
  checkInsThisSession = 0
  checkOutsThisSession = 0
}

export const recordCheckInThisSession = () => {
  checkInsThisSession += 1
}

export const recordCheckOutThisSession = () => {
  checkOutsThisSession += 1
}

const getSessionMetrics = () => ({
  uptimeMs: sessionStartedAt == null ? 0 : Date.now() - sessionStartedAt,
  checkInsThisSession,
  checkOutsThisSession,
})

export const reportRosterLoaded = ({ rosterCount, checkedInCount, durationMs, ...context }) => {
  lastLoadedAt = Date.now()
  consecutiveFailures = 0

  recordPageAction('kiosk_roster_loaded', {
    ...context,
    rosterCount,
    checkedInCount,
    durationMs,
  })
}

export const reportRosterFailed = ({ error, durationMs, ...context }) => {
  consecutiveFailures += 1

  recordPageAction('kiosk_roster_failed', {
    ...context,
    consecutiveFailures,
    durationMs,
    // How long the operator has been working from a roster we could not refresh.
    // null means this kiosk never managed to load one at all.
    staleForMs: lastLoadedAt == null ? null : Date.now() - lastLoadedAt,
    status: get(error, 'response.status', null),
    message: getErrorMessage(error),
  })
}

export const reportSearchMiss = attributes => recordPageAction('kiosk_search_miss', attributes)

export const reportSessionHeartbeat = context =>
  recordPageAction('kiosk_session_heartbeat', {
    ...context,
    ...getSessionMetrics(),
  })

export const reportSessionStopped = (context, reason) =>
  recordPageAction('kiosk_session_stopped', {
    ...context,
    ...getSessionMetrics(),
    reason,
  })
